import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router';
import { useEvent } from '../api/events';
import { downloadCsv } from '../api/reports';
import {
  type DateRangeValue,
  DateRangePicker,
  rangeForPreset,
} from '../components/DateRangePicker';

export interface SalesContext {
  eventId: string;
  range: DateRangeValue;
}

const STORAGE_KEY_PREFIX = 'inkypay.sales.range.';

const TABS: Array<{ to: string; label: string; matchSuffix?: string }> = [
  { to: '', label: 'Overview' },
  { to: 'products', label: 'Products' },
  { to: 'categories', label: 'Categories' },
  { to: 'timeline', label: 'Timeline' },
  { to: 'transactions', label: 'Transactions' },
];

export function Sales() {
  const { eventId } = useParams<{ eventId: string }>();
  const event = useEvent(eventId ?? '');
  const location = useLocation();
  const qc = useQueryClient();
  const [range, setRange] = useState<DateRangeValue | null>(null);

  // Initialize range from localStorage or event-to-date default once the event
  // loads. Persist on change so navigating between sub-tabs preserves the
  // selection within a session.
  //
  // For preset-based ranges (today / yesterday / event-to-date), recompute
  // the from/to from the preset instead of trusting the cached dates — a
  // "Today" range cached yesterday would otherwise re-query yesterday's
  // dates today and look broken.
  useEffect(() => {
    if (!eventId || !event.data) return;
    if (range) return;
    const args = {
      eventStartsOn: event.data.startsOn,
      eventTimezone: event.data.timezone,
    };
    const cached = localStorage.getItem(`${STORAGE_KEY_PREFIX}${eventId}`);
    if (cached) {
      try {
        const parsed = JSON.parse(cached) as DateRangeValue;
        if (parsed.preset && parsed.preset !== 'custom') {
          setRange(rangeForPreset(parsed.preset, args));
        } else {
          setRange(parsed);
        }
        return;
      } catch {
        // fall through to default
      }
    }
    setRange(rangeForPreset('event', args));
  }, [eventId, event.data, range]);

  useEffect(() => {
    if (!eventId || !range) return;
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${eventId}`, JSON.stringify(range));
  }, [eventId, range]);

  const activeTab = useMemo(() => {
    if (!eventId) return '';
    const base = `/events/${eventId}/sales`;
    if (location.pathname === base) return '';
    const sub = location.pathname.slice(base.length + 1);
    return sub.split('/')[0] ?? '';
  }, [eventId, location.pathname]);

  if (!eventId) return null;

  return (
    <div className="page">
      <div className="page-head" style={{ flexWrap: 'wrap', gap: 16 }}>
        <h1>Sales</h1>
        <div className="page-actions" style={{ flexWrap: 'wrap', gap: 12 }}>
          {range && event.data && (
            <DateRangePicker
              value={range}
              onChange={setRange}
              eventStartsOn={event.data.startsOn}
              eventTimezone={event.data.timezone}
            />
          )}
          {range && (
            <div style={{ display: 'inline-flex', gap: 6 }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  qc.invalidateQueries({ queryKey: ['event', eventId, 'reports'] })
                }
              >
                Refresh
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => downloadCsv(eventId, 'items', range)}
              >
                CSV: items
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => downloadCsv(eventId, 'transactions', range)}
              >
                CSV: transactions
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="tabs" style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border-1)' }}>
        {TABS.map((t) => (
          <Link
            key={t.label}
            to={t.to ? `/events/${eventId}/sales/${t.to}` : `/events/${eventId}/sales`}
            className={`tab${activeTab === t.to ? ' active' : ''}`}
          >
            {t.label}
          </Link>
        ))}
      </div>
      <div style={{ marginTop: 16 }}>
        {range ? <Outlet context={{ eventId, range } satisfies SalesContext} /> : null}
      </div>
    </div>
  );
}
